import * as m from "@paraglide/messages"
import type { SupportedLocale as Lang } from "@site-types/content"
import { useEffect, useRef, useState } from "react"
import { loadTurnstileScript, resolveTurnstileSiteKey } from "./turnstile"

type TurnstileWidgetProps = {
  lang: Lang
  onToken: (token: string | null) => void
}

/**
 * Renders the Turnstile challenge and hands its token up to the form.
 *
 * Nothing renders at all until a site key is configured, so the form behaves
 * exactly as it did before the keys land.
 *
 * A visitor who cannot reach Cloudflare — a blocker, a corporate proxy — gets no
 * token and would otherwise meet a bare "something went wrong" after writing out
 * an enquiry. The failure is surfaced here instead, next to the widget, while
 * they can still act on it.
 */
export function TurnstileWidget({ lang, onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const siteKey = resolveTurnstileSiteKey()

  // The callback identity would otherwise re-run the effect and re-render the
  // widget on every keystroke in the form above it.
  const onTokenRef = useRef(onToken)
  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  useEffect(() => {
    if (!siteKey) return

    let widgetId: string | undefined
    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language: lang,
          callback: (token: string) => {
            setFailed(false)
            onTokenRef.current(token)
          },
          "error-callback": () => {
            setFailed(true)
            onTokenRef.current(null)
          },
          "expired-callback": () => onTokenRef.current(null),
        })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [siteKey, lang])

  if (!siteKey) return null

  return (
    <div className="mx-auto mb-6 flex flex-col items-center gap-2">
      <div
        ref={containerRef}
        data-id="contact-turnstile"
      />
      {failed && (
        <p
          data-id="contact-turnstile-error"
          className="text-[var(--pink)]"
        >
          {m.contact_formVerification_error({}, { locale: lang })}
        </p>
      )}
    </div>
  )
}
